import type { RuleView } from "../../shared/api.js";
import type { AppContext } from "../Page.js";
import { h } from "../dom.js";
import { stackDetail, stackName } from "../format.js";
import { Dialog } from "./Dialog.js";
import { StackPicker } from "./StackPicker.js";
import { stackIcon } from "./StackIcon.js";

/** Create or edit one stocking rule in a dialog. Resolves with the saved rule, or null. */
export function editRule(ctx: AppContext, existing: RuleView | null): Promise<RuleView | null> {
  return new Promise((resolve) => {
    const dialog = new Dialog(existing ? "Edit rule" : "New rule", true);
    const picker = existing ? null : new StackPicker(ctx);
    const num = (value: number, min = 0) => h("input", { type: "number", min: String(min), value: String(value), required: true });
    const min = num(existing?.min ?? 64);
    const target = num(existing?.target ?? 128);
    const batch = num(existing?.batch ?? 64, 1);
    const priority = h("input", { type: "number", value: String(existing?.priority ?? 0) });
    const enabled = h("input", { type: "checkbox", checked: existing?.enabled ?? true });
    const critical = h("input", { type: "checkbox", checked: existing?.critical ?? false });
    const cpuNames = new Set([...ctx.live.cpus.map((c) => c.name), ...(existing?.cpus ?? [])]);
    const cpuBoxes = [...cpuNames]
      .filter((n) => !n.startsWith("#"))
      .map((name) => ({ name, box: h("input", { type: "checkbox", checked: existing?.cpus.includes(name) ?? false }) }));
    const error = h("p", { class: "error" });

    let saved: RuleView | null = null;
    dialog.el.addEventListener("close", () => resolve(saved));

    const stackSection = existing
      ? h("div", { class: "picker-chosen" }, stackIcon(existing.stack, "lg"), h("strong", null, stackName(existing.stack)), " ", h("span", { class: "muted" }, stackDetail(existing.stack)))
      : picker!.el;

    const form = h(
      "form",
      {
        class: "rule-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          error.textContent = "";
          const body: Record<string, unknown> = {
            min: Number(min.value),
            target: Number(target.value),
            batch: Number(batch.value),
            priority: Number(priority.value),
            enabled: enabled.checked,
            critical: critical.checked,
            cpus: cpuBoxes.filter((c) => c.box.checked).map((c) => c.name),
          };
          if (!existing) {
            if (!picker!.value) {
              error.textContent = "Pick an item, fluid or essentia first.";
              return;
            }
            body.stack = picker!.value;
          }
          try {
            saved = existing ? await ctx.api.updateRule(existing.id, body) : await ctx.api.createRule(body);
            ctx.toast(existing ? "Rule saved" : "Rule added");
            dialog.close();
          } catch (err) {
            error.textContent = (err as Error).message;
          }
        },
      },
      h("h3", null, "What to stock"),
      stackSection,
      existing?.groupName
        ? h("p", { class: "muted small" }, `This rule comes from the group ${existing.groupName}. Saving it here makes it a rule of its own, which the group no longer changes.`)
        : null,
      h("h3", null, "Amounts"),
      h(
        "div",
        { class: "row" },
        h("label", null, "Craft when below (min)", min),
        h("label", null, "Craft up to (target)", target),
        h("label", null, "Most per request (batch)", batch),
        h("label", null, "Priority", priority),
      ),
      h("p", { class: "muted small" }, "Fluids are in mB (1000 mB = 1 bucket). Higher priority rules are served first."),
      h("div", { class: "row" }, h("label", { class: "check" }, enabled, "Enabled"), h("label", { class: "check" }, critical, "Critical (stocked by the computer when the app is offline)")),
      cpuBoxes.length
        ? h("fieldset", null, h("legend", null, "Only use these CPUs (none checked = any)"), h("div", { class: "row wrap" }, ...cpuBoxes.map((c) => h("label", { class: "check" }, c.box, c.name))))
        : null,
      error,
    );
    dialog.body.appendChild(form);
    dialog.footer.append(
      h("button", { type: "button", onclick: () => dialog.close() }, "Cancel"),
      h("button", { type: "button", class: "primary", onclick: () => form.requestSubmit() }, existing ? "Save" : "Add rule"),
    );
    dialog.open();
    picker?.focus();
  });
}
